import React, { useRef, useState } from 'react';
import {
  Modal,
  BlockStack,
  InlineStack,
  Text,
  Banner,
  TextField,
  Spinner,
  Divider
} from '@shopify/polaris';

const money = value => Number(value || 0).toFixed(2);
const MIN_REASON_LENGTH = 4;
const AMOUNT_PATTERN = /^\d+(\.\d{1,2})?$/;

export default function OrderEditReview({
  open,
  preview,
  status,
  error,
  discount,
  onDiscountChange,
  onBack,
  onSave,
  saving,
  saveError,
  draftErrors = []
}) {
  const [touched, setTouched] = useState({ amount: false, reason: false });
  const touch = field => setTouched(current => ({ ...current, [field]: true }));

  // The discount inputs live outside the ready gate so they stay mounted while a
  // new preview loads; the last good preview supplies the figures meanwhile.
  const lastPreviewRef = useRef(null);
  if (preview) lastPreviewRef.current = preview;
  const shown = preview || lastPreviewRef.current;
  const ready = status === 'ready' && Boolean(preview) && draftErrors.length === 0;
  const rawAmount = discount.amount.trim();
  const amountInvalid = rawAmount !== '' && !AMOUNT_PATTERN.test(rawAmount);
  const amount = amountInvalid ? 0 : Number(rawAmount) || 0;
  const maxDiscount = shown ? Number(shown.max_discount) || 0 : 0;
  const blockingErrors = ready ? preview.blocking_errors || [] : [];

  let amountError = '';
  if (amountInvalid) amountError = 'Enter a valid discount amount';
  else if (shown && amount > maxDiscount) amountError = `Up to ${money(maxDiscount)}`;
  const reasonError =
    amount > 0 && discount.reason.trim().length < MIN_REASON_LENGTH
      ? `Enter a reason of at least ${MIN_REASON_LENGTH} characters.`
      : '';

  const canSave =
    Boolean(ready) && !saving && blockingErrors.length === 0 && !amountError && !reasonError;

  // Errors are shown only after a field is blurred. Save stays disabled while
  // any of them applies, and clicking the disabled Save blurs the focused field
  // first, so no separate "tried to save" state is needed.
  const shownAmountError = touched.amount ? amountError : '';
  const shownReasonError = touched.reason ? reasonError : '';

  const changedLines = shown
    ? (shown.lines || []).filter(
        line => Number(line.before_price) !== Number(line.after_price) || Number(line.discount_share) > 0
      )
    : [];

  return (
    <Modal
      open={open}
      onClose={onBack}
      title="Review changes"
      primaryAction={{ content: 'Save changes', onAction: onSave, disabled: !canSave }}
      secondaryActions={[{ content: 'Back to editing', onAction: onBack }]}
    >
      <Modal.Section>
        <BlockStack gap="400">
          {status === 'loading' && (
            <InlineStack gap="200" blockAlign="center">
              <Spinner size="small" accessibilityLabel="Loading" />
              <Text as="span" tone="subdued">Calculating changes</Text>
            </InlineStack>
          )}

          {status === 'error' && (
            <Banner tone="critical" title="Could not calculate the changes">
              <p>{error}</p>
            </Banner>
          )}

          {saveError && (
            <Banner tone="critical" title="Changes were not saved">
              <p>{saveError}</p>
            </Banner>
          )}

          {blockingErrors.length > 0 && (
            <Banner tone="critical" title="Fix these before saving">
              <BlockStack gap="100">
                {blockingErrors.map((message, index) => <p key={`${index}-${message}`}>{message}</p>)}
              </BlockStack>
            </Banner>
          )}

          {draftErrors.length > 0 && (
            <Banner tone="critical" title="Fix these before saving">
              <BlockStack gap="100">
                {draftErrors.map(message => <p key={message}>{message}</p>)}
              </BlockStack>
            </Banner>
          )}

          {ready && preview.warnings?.length > 0 && (
            <Banner tone="warning" title="Check before saving">
              <BlockStack gap="100">
                {preview.warnings.map((message, index) => <p key={`${index}-${message}`}>{message}</p>)}
              </BlockStack>
            </Banner>
          )}

          {shown && (
            <>
              <BlockStack gap="100">
                <Text as="span" tone="subdued">New total</Text>
                <InlineStack gap="300" blockAlign="baseline">
                  <Text as="p" variant="headingLg">{money(shown.totals.after)}</Text>
                  <Text as="span" tone="subdued">Previous total</Text>
                  <Text as="span" tone="subdued">{money(shown.totals.before)}</Text>
                </InlineStack>
              </BlockStack>

              <BlockStack gap="200">
                <Text as="h3" variant="headingSm">Changes</Text>
                {(shown.changes || []).map((change, index) => (
                  <Text as="p" key={`${index}-${change.label}`}>{change.label}</Text>
                ))}
                {changedLines.map(line => (
                  <BlockStack gap="050" key={`${line.line_item_id}-${line.added_index}`}>
                    {Number(line.before_price) !== Number(line.after_price) && (
                      <Text as="p" tone="subdued">
                        {`${line.name}: ${money(line.before_price)} to ${money(line.after_price)}`}
                      </Text>
                    )}
                    {Number(line.discount_share) > 0 && (
                      <Text as="p" tone="subdued">
                        {`${line.name}: discount -${money(line.discount_share)}`}
                      </Text>
                    )}
                  </BlockStack>
                ))}
              </BlockStack>
            </>
          )}

          <Divider />

          <InlineStack gap="400" align="start" blockAlign="start" wrap>
            <TextField
              label="Discount amount"
              type="number"
              min={0}
              value={discount.amount}
              onChange={value => onDiscountChange({ ...discount, amount: value })}
              onBlur={() => touch('amount')}
              error={shownAmountError || undefined}
              helpText={shownAmountError || !shown ? undefined : `Up to ${money(maxDiscount)}`}
              autoComplete="off"
              disabled={saving}
            />
            <TextField
              label="Reason"
              value={discount.reason}
              onChange={value => onDiscountChange({ ...discount, reason: value })}
              onBlur={() => touch('reason')}
              error={shownReasonError || undefined}
              autoComplete="off"
              disabled={saving}
            />
          </InlineStack>

          {ready && (
            <>
              <Divider />

              <BlockStack gap="100">
                <Text as="p">{`Already paid ${money(preview.payment.paid_amount)}`}</Text>
                <Text as="p" variant="headingSm">
                  {preview.payment.collect_on_delivery
                    ? `Collect on delivery ${money(preview.totals.after)}`
                    : `Customer owes ${money(preview.payment.amount_due)}`}
                </Text>
              </BlockStack>
            </>
          )}
        </BlockStack>
      </Modal.Section>
    </Modal>
  );
}
